/**
 * ClientSelector — seletor global de clientes do header.
 *
 * REGRA:
 * - Mostra somente clientes aos quais o usuário possui membership.
 * - Não confia em client_id externo.
 * - "Todos os clientes" é uma opção explícita, NUNCA default.
 * - Contexto ≠ membership (não modifica membership).
 */
import { useState, useMemo, useRef, useEffect } from "react";
import { useClient } from "@/context/ClientContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Check,
  ChevronDown,
  Search,
  Users,
} from "lucide-react";

export default function ClientSelector() {
  const {
    clients,
    selectedClientIds,
    isMulti,
    isAll,
    loading,
    toggleClient,
    selectAll,
    selectNone,
    restoreValidSelection,
  } = useClient();

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const searchRef = useRef(null);

  useEffect(() => {
    if (open && searchRef.current) {
      searchRef.current.focus();
    }
  }, [open]);

  const filteredClients = useMemo(() => {
    if (!search.trim()) return clients;
    const q = search.toLowerCase();
    return clients.filter(
      (c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.code || "").toLowerCase().includes(q) ||
        (c.cnpj || "").toLowerCase().includes(q)
    );
  }, [clients, search]);

  const selectedCount = selectedClientIds.length;
  const totalClients = clients.length;

  const displayLabel = useMemo(() => {
    if (loading) return "Carregando...";
    if (selectedCount === 0) return "Nenhum cliente selecionado";
    if (isAll) return "Todos os clientes";
    if (selectedCount === 1) {
      const c = clients.find((cl) => cl.id === selectedClientIds[0]);
      return c?.name || "Cliente";
    }
    return `${selectedCount} clientes selecionados`;
  }, [selectedClientIds, isAll, selectedCount, clients, loading]);

  const displayCode = useMemo(() => {
    if (selectedCount === 1 && !isAll) {
      const c = clients.find((cl) => cl.id === selectedClientIds[0]);
      return c?.code;
    }
    return null;
  }, [selectedClientIds, isAll, clients]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center gap-2 h-9 px-3 max-w-xs cursor-pointer"
          data-testid="client-selector-trigger"
          title="Selecionar clientes"
        >
          <Users size={16} className="text-muted-foreground" />
          <span className="font-medium truncate">{displayLabel}</span>
          {displayCode && (
            <Badge variant="secondary" className="text-xs">
              {displayCode}
            </Badge>
          )}
          <ChevronDown size={14} className="text-muted-foreground ml-auto" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="start" className="w-80 p-0 border shadow-lg" data-testid="client-selector-popover">
        <div className="p-3 border-b border-border">
          <div className="relative">
            <Search size={14} className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              ref={searchRef}
              type="text"
              placeholder="Buscar cliente..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-8 pl-8 text-sm"
              data-testid="client-selector-search"
            />
          </div>
        </div>

        <div className="max-h-80 overflow-y-auto scrollbar-thin">
          <button
            type="button"
            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-accent transition-colors"
            onClick={() => {
              if (isAll) { selectNone(); } else { selectAll(); }
            }}
            data-testid="client-select-all"
          >
            <span className="font-medium">Todos os clientes</span>
            {isAll ? <Check size={14} className="text-primary" /> : <span className="w-4 h-4" />}
          </button>

          {filteredClients.length === 0 ? (
            <div className="px-3 py-4 text-sm text-muted-foreground text-center">Nenhum cliente encontrado</div>
          ) : (
            filteredClients.map((client) => {
              const isSelected = selectedClientIds.includes(client.id);
              return (
                <button
                  key={client.id}
                  type="button"
                  className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-accent transition-colors"
                  onClick={() => {
                    // Toggle individual (checkbox): permite seleção múltipla
                    // aditiva e desmarcar individual. Sem fallback silencioso.
                    toggleClient(client.id);
                  }}
                  data-testid={`client-option-${client.id}`}
                >
                  <div className="flex flex-col min-w-0 flex-1">
                    <span className={`text-sm font-medium truncate ${isSelected ? "text-primary" : ""}`}>{client.name}</span>
                    {client.code && <span className="text-xs text-muted-foreground truncate">{client.code}</span>}
                  </div>
                  {isSelected && <Check size={14} className="text-primary shrink-0" />}
                </button>
              );
            })
          )}
        </div>

        {selectedCount > 0 && (
          <div className="border-t border-border p-2 flex items-center justify-between gap-2">
            <button
              type="button"
              className="flex-1 text-center text-xs text-muted-foreground hover:text-foreground hover:bg-accent py-1.5 rounded transition-colors"
              onClick={() => { selectNone(); setOpen(false); }}
              data-testid="client-deselect-all"
            >Desmarcar todos</button>
            <button
              type="button"
              className="flex-1 text-center text-xs text-muted-foreground hover:text-foreground hover:bg-accent py-1.5 rounded transition-colors"
              onClick={() => { restoreValidSelection(); setOpen(false); }}
              title="Descarta seleções que saíram do seu escopo e recarrega os clientes autorizados"
              data-testid="client-restore-valid"
            >Restaurar seleção válida</button>
          </div>
        )}

        <div className="border-t border-border px-3 py-2 text-xs text-muted-foreground">
          {selectedCount} de {totalClients} selecionado(s)
        </div>
      </PopoverContent>
    </Popover>
  );
}